import Sentry from '@/services/sentry'
import {
  isDesktopOfficeHost,
  isPowerPointApiSupported,
  readDocumentSetting,
  readSelectedSlideId,
  saveDocumentSetting,
} from '@/services/office/helpers'
import {
  SLIDE_TAG_PRESENTATION_KEY,
  SLIDE_TAG_SLIDE_KEY,
  canReadSlideTags,
  queueBindingTagRead,
  readTagsAtSlideOnce,
} from '@/services/office/slide-tags'
import { createDebug } from '@/utils/debug'
import { withTimeout } from '@/utils/promise'
import { isPptOnlineAddin, readOwnSlideTokenFromFrameName, readOwnAddinInstanceId } from '@/utils/ppt-online.utils'
import { savePptCommonSlideBinding, savePptOnlineBinding } from '@/services/office/ppt-online-binding'
import { generateAddinInstanceId, savePptInstanceBinding, toSlideToken } from '@/services/office/ppt-instance-binding'
import { persistInviteCodeAfterInsert, persistInviteCodeBeforeInsert } from '@/services/office/ppt-auto-join-editor'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:multiple-select'
const log = createDebug('ppt:multiple-select')

// Ids that come back from a PowerPoint slide tag or a Vue route param are STRINGS. The ids
// the rest of the app holds are numbers, and PresentationDetailUtils gates the PowerPoint
// binding on a STRICT comparison:
//   store.state.powerPointPresentationId === res.body.id   // res.body.id is a number
// A string binding silently loses that comparison, so the editor discards the frame's
// resolved slide and falls back to res.body.activeSlide — the server-wide active slide,
// which is the same in every frame. Every cloned frame then renders the same AhaSlides
// slide, defeating the entire point of the per-slide tags. Anything that flows a tag value
// or route param into the store or document.settings must pass through here first.
//
// Non-numeric input (including null/undefined, which callers use to CLEAR a binding and
// which Number() would happily turn into 0) passes through untouched.
export const toNumericId = (value) => {
  if (value === null || value === undefined || value === '') {
    return value
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : value
}

// AHA-43111: the Common-API slide id derived from a Rich API slide id, with NO Office call.
//
// On PowerPoint web a Rich API slide id is shaped "<commonId>#<token>" — the same shape
// readTagsBySlideToken already relies on to match its per-frame token against `[1]`. The
// numeric prefix `[0]` is exactly what getSelectedDataAsync(SlideRange) returns for that
// slide (the id probe measured 257 from both APIs for the same physical slide), which makes
// it the key of the read-view bridge in ppt-online-binding.
//
// Deriving it beats reading it. getSelectedDataAsync is DOCUMENT-GLOBAL — it answers with
// whatever the user has selected right now — so keying off it means a thumbnail click
// mid-insert writes one slide's binding under another slide's id. A Rich id we already hold
// names the slide we actually just created or tagged, so the key and the binding can never
// come from different slides. It also costs zero round-trips inside the insert loop.
//
// Desktop ids carry no '#', so this returns the id unchanged there — harmless, because every
// consumer (savePptCommonSlideBinding) is a no-op off the web.
export const toCommonSlideId = (richSlideId) => {
  if (richSlideId === null || richSlideId === undefined || richSlideId === '') {
    return null
  }
  return String(richSlideId).split('#')[0]
}

// Slide.exportAsBase64 — the donor export the whole clone trick depends on.
const BULK_ADD_API_VERSION = '1.8'

// Raised when a just-issued insert cannot be matched to a new slide id. Thrown identically by
// every insert path, so it is one string rather than three.
const UNRESOLVED_INSERT_ERROR = 'Could not identify the inserted PowerPoint slide'

// The Sentry breadcrumb category shared by every Office-API entry point in this module.
const OFFICE_BREADCRUMB_CATEGORY = 'ppt-office-api'

// AHA-46329: the insert budget MUST scale with the selection. The loop costs a couple of
// context.sync() round-trips plus a base64 pptx parse per inserted slide, so a flat 20s
// budget was blown by any large selection (the prominent "Select all" on a 40-slide deck),
// which timed out, rolled every inserted slide back, and made the feature unusable above
// some deck size. Budget = base + per-slide allowance; it is a runaway guard, not a
// latency target, so it is deliberately generous.
const INSERT_TIMEOUT_BASE_MS = 15000
const INSERT_TIMEOUT_PER_SLIDE_MS = 6000
const insertTimeoutMs = (slideCount) => {
  return INSERT_TIMEOUT_BASE_MS + (INSERT_TIMEOUT_PER_SLIDE_MS * slideCount)
}
// Bound on how long the failure path waits for a timed-out `run` to settle before rolling
// back regardless (see waitForSettleWithGrace below).
const SETTLE_GRACE_MS = 5000

// Cold-load host-slide tag read (readSlideBindingFromHostSlide). On web several frames boot at
// once and their PowerPoint.run batches contend — a stalled context.sync() has no natural
// timeout, so it would hang the whole cold-load chain and strand the frame on the dashboard.
// Bound each attempt and retry a few times so a transient stall recovers and a permanent one
// falls through to the cold-load fallbacks instead of hanging forever.
const BINDING_READ_TIMEOUT_MS = 2500
const BINDING_READ_MAX_ATTEMPTS = 3
const BINDING_READ_RETRY_DELAY_MS = 600
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// There is no Office.js API to embed a content add-in onto a new slide, so a new slide
// carrying the add-in can only be made by cloning one that already has it. That needs
// exportAsBase64 (1.8), which is absent on iPad and on every volume-licensed perpetual
// Office (incl. 2024 LTSC). Callers must check this BEFORE offering the flow, so an
// unsupported host never reaches a dead end.
export const isBulkSlideAddSupported = () => {
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return false
  }
  return isPowerPointApiSupported(BULK_ADD_API_VERSION)
}

// Which slide does this add-in frame live on? Office.js has no direct answer;
// getSelectedDataAsync(SlideRange) is the closest signal — on mount, the frame's own
// slide is the selected one. Resolves the 1-based index, which the PowerPoint.run
// batch turns into a canonical slide id (the ids the two APIs hand out differ in shape,
// so never compare them directly).
//
// `caller` only labels the debug log: with seven cloned frames booting at once, every one
// of them calls this and an unlabelled line cannot be attributed. The elapsed time and the
// resolved index are logged together on purpose — the index is the diagnostic that matters,
// since several frames resolving the SAME index is the selection-is-not-per-frame defect
// on paper, and the timing alone would only show the jitter that scrambles which wrong
// answer each frame gets.
const readHostSlideIndex = (caller = 'unknown') => new Promise((resolve, reject) => {
  const startedAt = performance.now()
  readSelectedSlideId((selectedId, error, slides) => {
    const elapsedMs = Math.round(performance.now() - startedAt)
    const index = slides && slides[0] && slides[0].index
    if (error || !index) {
      log(`readHostSlideIndex[${caller}] failed after ${elapsedMs}ms`, error)
      reject(new Error(error || 'Could not identify the current PowerPoint slide'))
      return
    }
    log(`readHostSlideIndex[${caller}] -> index ${index} (selected id ${selectedId}) in ${elapsedMs}ms`)
    resolve(index)
  })
})

// Waits for `promise` to settle, but only up to `ms` — used on the failure path to give a
// timed-out `run` a chance to finish before rollback reads its final state, WITHOUT letting
// that wait become unbounded if the batch never settles at all (e.g. a frozen/crashed host,
// exactly what the insert timeout was meant to guard against). `promise`'s own outcome is
// swallowed unconditionally — attaching `.catch` here immediately, not after the grace
// period — so it can never surface as an unhandled rejection regardless of whether the
// grace timer or the promise settles first.
const waitForSettleWithGrace = (promise, ms) => {
  const settled = promise.catch(() => {})
  let timer = null
  const grace = new Promise((resolve) => { timer = setTimeout(resolve, ms) })
  return Promise.race([settled, grace]).finally(() => clearTimeout(timer))
}

// Loads the current slide id list. Called ONCE after each insert: the newly inserted slide
// is identified by DIFFING these ids against the running list we already hold — resolving
// it by index would break the moment the user reorders slides mid-flight. The "before"
// load each iteration used to do is redundant (the previous iteration's "after" IS this
// iteration's "before"), and since every load re-fetches the WHOLE slide collection it was
// costing O(N^2) proxy traffic plus an extra sync round-trip per inserted slide.
const loadSlideIds = async (context, slides) => {
  slides.load('items/id')
  await context.sync()
  return slides.items.map((slide) => slide.id)
}

// The picture is cosmetic, so a donor that cannot be patched is inserted exactly as exported.
// The patcher (and its zip library) is loaded on first use, so it stays out of the editor bundle.
const withAddinSnapshot = async (donorBase64, snapshotPng) => {
  if (!snapshotPng) {
    return donorBase64
  }
  try {
    const { replaceAddinSnapshot } = await import('@/services/office/addin-snapshot-patch')
    return replaceAddinSnapshot(donorBase64, snapshotPng)
  } catch (error) {
    log('add-in snapshot not patched', error)
    Sentry.addBreadcrumb({
      category: OFFICE_BREADCRUMB_CATEGORY,
      message: `add-in snapshot not patched: ${error.message}`,
      level: 'warning',
    })
    return donorBase64
  }
}

// AHA-48826: the donor with its add-in instance id replaced, or the donor as exported when the
// rewrite fails (the clone then shares the host's instance, which the read side reports as
// ambiguous rather than resolving wrongly). Loaded lazily, like the snapshot patch.
const withAddinInstanceId = async (donorBase64, instanceId) => {
  if (!instanceId) {
    return donorBase64
  }
  try {
    const { replaceAddinInstanceId } = await import('@/services/office/addin-snapshot-patch')
    return replaceAddinInstanceId(donorBase64, instanceId)
  } catch (error) {
    log('add-in instance id not rewritten', error)
    Sentry.addBreadcrumb({
      category: OFFICE_BREADCRUMB_CATEGORY,
      message: `add-in instance id not rewritten: ${error.message}`,
      level: 'warning',
    })
    return donorBase64
  }
}

// Inserts the donor with its add-in snapshot patched in. withAddinSnapshot only catches a patch
// that throws; PowerPoint can still reject the patched package at sync, and a cosmetic picture
// must not fail the whole Add, so that one insert is retried without the picture.
//
// AHA-48826 (web): `instanceId` gives the clone its own add-in instance id first, so its frame can
// identify itself in the Slide Show. That rewrite is not cosmetic, so it is the BASE the retry falls
// back to, not part of what the retry drops — and it adds no round trip: a donor with no snapshot to
// patch is inserted in one sync exactly as before, with or without the new id. Resolves to the
// instance id the inserted clone carries, or null when the rewrite did not take.
const insertDonorWithSnapshot = async (context, donorBase64, snapshotPng, targetSlideId, instanceId = null) => {
  const { presentation } = context
  const base = await withAddinInstanceId(donorBase64, instanceId)
  const appliedInstanceId = base !== donorBase64 ? instanceId : null
  const patched = await withAddinSnapshot(base, snapshotPng)
  if (patched !== base) {
    // On the web the previous clone's tag is still queued. Sent on its own, its failure fails the
    // Add as before, instead of passing for a rejected package and being dropped by the retry.
    await context.sync()
  }
  presentation.insertSlidesFromBase64(patched, { targetSlideId })
  try {
    await context.sync()
  } catch (error) {
    if (patched === base) {
      throw error
    }
    log('patched donor rejected, inserting it without the snapshot', error)
    Sentry.addBreadcrumb({
      category: OFFICE_BREADCRUMB_CATEGORY,
      message: `patched donor rejected: ${error.message}`,
      level: 'warning',
    })
    presentation.insertSlidesFromBase64(base, { targetSlideId })
    await context.sync()
  }
  return appliedInstanceId
}

const tagSlide = (slide, presentationId, ahaSlideId) => {
  slide.tags.add(SLIDE_TAG_PRESENTATION_KEY, String(presentationId))
  slide.tags.add(SLIDE_TAG_SLIDE_KEY, String(ahaSlideId))
}

// Which id in `after` is the slide we just inserted? insertSlidesFromBase64 places it
// immediately after targetSlideId, so it is the id in that slot — NOT merely "the first new
// id in document order" (a bare diff), which would pick up a slide the USER created ABOVE
// the host mid-insert (a co-author on Office web, a Ctrl+M in the thumbnail pane). That slide
// would then be tagged with our binding, chained as the next target, and DELETED on rollback,
// destroying the user's own content. Falls back to the diff only if the positional slot is
// somehow not a new id. Returns null when nothing new is found at all.
const resolveInsertedId = (after, before, targetSlideId) => {
  const targetAt = after.indexOf(targetSlideId)
  const positional = targetAt === -1 ? null : after[targetAt + 1]
  if (positional && !before.includes(positional)) {
    return positional
  }
  return after.find((id) => !before.includes(id)) || null
}

// Removes both binding keys from a slide. delete() is a no-op when the key is absent, so
// this needs no existence check.
const clearTagsOnSlide = (slide) => {
  slide.tags.delete(SLIDE_TAG_PRESENTATION_KEY)
  slide.tags.delete(SLIDE_TAG_SLIDE_KEY)
}

// Strips any binding tags off the donor slide BEFORE it is exported for cloning.
//
// A slide's tags live in its part of the .pptx, so they ride through
// exportAsBase64 → insertSlidesFromBase64: a donor that still carries a PREVIOUS Add's
// {presentationId, slideId} produces clones that materialise holding the DONOR's binding,
// not a blank one. An add-in that activates on such a clone (Office web activates on the
// insert's sync) can read that wrong binding before our own per-clone tag overwrites it —
// a wrong slide rendered, not merely a missing tag. True on every SECOND Add from the same
// slide and after any selectSingle rebind. Runs in its own batch so the export below is
// guaranteed to see the post-strip state; the host's correct {presentationId, slideIds[0]}
// tag is re-applied in the main batch. Best-effort: a strip failure must not abort the Add.
const stripHostSlideTags = async (hostSlideIndex) => {
  try {
    await window.PowerPoint.run(async (context) => {
      clearTagsOnSlide(context.presentation.slides.getItemAt(hostSlideIndex - 1))
      await context.sync()
    })
    log('stripped stale donor tags')
  } catch (error) {
    log('strip donor tags failed', error)
    Sentry.captureException(error)
  }
}

// Undoes a failed Add: deletes the slides we inserted, and strips the tags off the host
// slide. Best-effort — a failure to roll back must not mask the original error.
//
// Deleting the clones stops a partial failure leaving orphan slides. Stripping the HOST
// tags matters because the batch may have tagged the host with {presentationId, slideIds[0]}
// before failing (or completed late, during the settle grace): left in place, that tag —
// the highest-precedence source on load — would open the presentation in presenting mode on
// the next load for an Add the user was told had FAILED. The host slide itself is the user's
// own and is never deleted; only its tags are cleared. Its settings binding is untouched by
// a failed Add, so the host frame still resolves correctly from settings on reload.
//
// Every id is resolved with getItemOrNullObject, NOT getItem: an id can legitimately be gone
// by rollback time (the user hit Ctrl+Z during the insert window), and getItem throws at
// sync for an unknown id — which, with the deletes batched, would fail the WHOLE rollback
// and orphan every other inserted slide. isNullObject lets an already-gone slide be skipped.
const rollbackInsertedSlides = async (cloneIds, hostSlideId) => {
  if (!cloneIds.length && !hostSlideId) {
    return
  }
  try {
    await window.PowerPoint.run(async (context) => {
      const clones = cloneIds.map((id) => {
        const slide = context.presentation.slides.getItemOrNullObject(id)
        slide.load('isNullObject')
        return slide
      })
      const host = hostSlideId
        ? context.presentation.slides.getItemOrNullObject(hostSlideId)
        : null
      if (host) {
        host.load('isNullObject')
      }
      await context.sync()

      clones.forEach((slide) => {
        if (!slide.isNullObject) {
          slide.delete()
        }
      })
      if (host && !host.isNullObject) {
        clearTagsOnSlide(host)
      }
      await context.sync()
    })
    log('rolled back inserted slides', cloneIds)
  } catch (error) {
    log('rollback failed', error)
    Sentry.captureException(error)
  }
}

// The document.settings key holding a frame's {presentationId, slideId} binding. Declared
// here as well as read in office/index.js because the desktop insert path below writes it
// directly — it cannot import index.js, which imports this module.
const DATA_SLIDE_SETTING_KEY = 'dataSlidePPT'

// Resolves the host slide's id and the current slide-id list in one batch. The desktop path
// needs both before its loop starts, and unlike the web path it has no single enclosing
// batch to piggy-back the load onto.
const readHostSlideState = async (hostSlideIndex) => {
  let hostSlideId = null
  let slideIdList = []
  await window.PowerPoint.run(async (context) => {
    const { slides } = context.presentation
    const hostSlide = slides.getItemAt(hostSlideIndex - 1)
    hostSlide.load('id')
    slides.load('items/id')
    await context.sync()
    hostSlideId = hostSlide.id
    slideIdList = slides.items.map((slide) => slide.id)
  })
  return { hostSlideId, slideIdList }
}

// Writes the frame's own binding back into document.settings. The desktop loop below leaves
// settings holding the LAST clone's binding, so the host frame — whose settings these
// actually are — must be restored before anything reads them again, on the success path and
// on the failure path alike.
const restoreHostBinding = async (presentationId, hostAhaSlideId) => {
  await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, {
    presentationId: toNumericId(presentationId),
    slideId: toNumericId(hostAhaSlideId),
  })
}

// AHA-47221 keep-host mode: everything the host slide needs to come back exactly as it was.
//
// Both sources are read, not just the tags. The tag is what a CLONE's load path reads and what
// outranks everything else on the host's own load; document.settings is what the desktop host
// frame reads, and the insert loop overwrites it once per clone. Neither can be derived from the
// other, and a frame bound through only one of them still has to end up bound.
//
// Never rejects: a binding that cannot be read is one that cannot be restored either, and that is
// not a reason to refuse an insert the user asked for.
const readHostBindingSnapshot = async (hostSlideIndex) => {
  let tags = null
  if (canReadSlideTags()) {
    try {
      tags = await readTagsAtSlideOnce(hostSlideIndex)
    } catch (error) {
      log('read host binding tags failed', error)
    }
  }
  // readDocumentSetting resolves null rather than rejecting, so this needs no guard of its own.
  const settings = await readDocumentSetting(DATA_SLIDE_SETTING_KEY)
  log('host binding snapshot', { tags, settings })
  return { tags, settings }
}

// Puts a snapshotted host binding back, by INDEX rather than by id: on the failure path the
// batch may have died before it resolved the host's id, and the index is stable either way —
// clones only ever land after the host, and rollback only deletes them.
//
// `restoreSettings` is a caller's decision rather than a property of the platform, though the
// slide path answers it with `desktop` (only the desktop loop moves settings, so only desktop has
// anything to put back). insertPresentationSlide below answers it differently on purpose: on the
// web the settings bag it leaves behind is the NEW slide's only identity, so it must survive the
// success path and be undone only on the failure one.
//
// Best-effort on every leg. On the success path the slides ARE in the deck, so failing the whole
// Add over a tag write would roll back work that succeeded; on the failure path the original
// error is the one the caller must see.
const restorePreservedHostBinding = async (hostSlideIndex, hostBinding, restoreSettings) => {
  if (!hostBinding) {
    return
  }
  const { tags, settings } = hostBinding
  if (tags && tags.presentationId && tags.slideId && canReadSlideTags()) {
    try {
      await window.PowerPoint.run(async (context) => {
        tagSlide(context.presentation.slides.getItemAt(hostSlideIndex - 1), tags.presentationId, tags.slideId)
        await context.sync()
      })
      log('restored host slide tags', tags)
    } catch (error) {
      log('restore host slide tags failed', error)
      Sentry.captureException(error)
    }
  }
  if (!restoreSettings) {
    return
  }
  try {
    await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, settings)
    log('restored host settings', settings)
  } catch (error) {
    log('restore host settings failed', error)
    Sentry.captureException(error)
  }
}

// AHA-46329 desktop insert: bakes each clone's binding into the donor BEFORE exporting it, so
// every inserted slide carries its OWN document.settings snapshot.
//
// Why this exists. Every frame — host and clones alike — used to resolve "which AhaSlides
// slide am I bound to?" by asking which PowerPoint slide it lives on, via
// getSelectedDataAsync. That call is DOCUMENT-GLOBAL: it returns the user's current
// selection, the same answer in every frame. Seven clones booting at staggered times
// therefore each sampled a moving selection, which is what produced the duplicated and
// missing slides ("slide 6 before slide 3, three copies of slide 3"). No amount of timing
// or load-speed work fixes that — at zero latency all seven frames simply agree on the
// same wrong answer.
//
// A cloned slide inherits a SNAPSHOT of the donor's document.settings as of the moment
// exportAsBase64 ran. So if settings hold slideIds[i] at export time, the clone comes up
// already holding slideIds[i] — per-clone identity, carried in the clone itself, with no
// selection lookup anywhere on the load path.
//
// DESKTOP ONLY, and only ever worked there. On the web document.settings are shared LIVE across
// every add-in frame (office-js #2765), so a booting clone reads the shared bag, never its baked
// copy — the bake buys nothing there, and the writes are not free: each is a settings.saveAsync
// into a bag the clone frames booting mid-loop are also writing, and saveDocumentSetting REJECTS
// on a failed save, which fails the whole insert and sends rollbackInsertedSlides in to delete
// every slide it just added ("slide appears, then vanishes"). The web keeps runWebInsert below,
// whose per-slide identity comes from the slide TAGS (edit view) and the common-slide localStorage
// bridge (read view) — neither of which needs settings.
//
// Cost: one export plus one settings round-trip PER clone, where the web path pays a single
// shared export. There is no cheaper way to vary the payload — the whole point is that each
// clone must carry a DIFFERENT snapshot, and a snapshot is only taken at export.
//
// Tags are still written, kept in step with the baked settings so the two can never disagree.
// The desktop LOAD path no longer reads them (see openSlideFromHostSlideTag).
const runDesktopInsert = async ({
  presentationId,
  slideIds,
  snapshotPngBySlideId,
  hostSlideIndex,
  insertedSlideIds,
  keepHostSlide,
  onHostResolved,
}) => {
  const { hostSlideId, slideIdList } = await readHostSlideState(hostSlideIndex)
  // Reported as soon as it is known so the caller's rollback can strip the host's tags even
  // if this throws part-way through.
  onHostResolved(hostSlideId)

  let knownSlideIds = slideIdList
  let targetSlideId = hostSlideId

  // keep-host mode clones the first selected slide too — see insertSlidesForSelection.
  const firstCloneIndex = keepHostSlide ? 0 : 1

  for (let i = firstCloneIndex; i < slideIds.length; i += 1) {
    // Persisted BEFORE the export, so this clone's snapshot carries this clone's binding.
    // eslint-disable-next-line no-await-in-loop
    await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, {
      presentationId: toNumericId(presentationId),
      slideId: toNumericId(slideIds[i]),
    })

    // Snapshotted into per-iteration consts: the batch closure below must not capture the
    // mutable running values (no-loop-func), same reason the web path snapshots `before`.
    const before = knownSlideIds
    const target = targetSlideId
    let insertedId = null
    let after = null

    // Sequential by design, exactly as on the web path: each insert targets the slide the
    // previous iteration created, so its id must be resolved before the next insert.
    // eslint-disable-next-line no-await-in-loop
    await window.PowerPoint.run(async (context) => {
      const { presentation } = context
      const { slides } = presentation

      // Re-exported every iteration — that IS the mechanism, not an oversight: each export
      // captures the settings snapshot written immediately above, and that snapshot is the only
      // thing making the clones differ.
      const donor = slides.getItemAt(hostSlideIndex - 1).exportAsBase64()
      await context.sync()

      await insertDonorWithSnapshot(context, donor.value, snapshotPngBySlideId[slideIds[i]], target)
      after = await loadSlideIds(context, slides)

      insertedId = resolveInsertedId(after, before, target)
      if (!insertedId) {
        throw new Error(UNRESOLVED_INSERT_ERROR)
      }
      insertedSlideIds.push(insertedId)

      tagSlide(slides.getItem(insertedId), presentationId, slideIds[i])
      await context.sync()
    })

    knownSlideIds = after
    targetSlideId = insertedId
    log(`clone ${i} inserted with slideId ${slideIds[i]}`)

    // AHA-43111 read-view bridge: key this clone's binding by the Common-API id derived from
    // the Rich id we JUST resolved for it, so the Slide Show — where the tags are unreadable —
    // can look it up. No Office call and no selection read: see toCommonSlideId.
    savePptCommonSlideBinding(toCommonSlideId(insertedId), { presentationId, slideId: slideIds[i] })
  }

  // keep-host mode: the host slide is not part of where this selection lands, so none of the
  // rebinding below applies to it. Its tags and its settings go back to what they were from the
  // pre-insert snapshot, which insertSlidesForSelection holds.
  if (keepHostSlide) {
    return
  }

  await window.PowerPoint.run(async (context) => {
    tagSlide(context.presentation.slides.getItem(hostSlideId), presentationId, slideIds[0])
    await context.sync()
  })

  // Read-view bridge for the host slide, keyed off the Rich id readHostSlideState resolved.
  savePptCommonSlideBinding(toCommonSlideId(hostSlideId), { presentationId, slideId: slideIds[0] })

  // Settings currently hold the last clone's binding; hand them back to the host frame.
  await restoreHostBinding(presentationId, slideIds[0])
}

// The web insert: ONE donor export, reused for every clone, all inside a single batch.
//
// Every clone therefore carries the same document.settings snapshot, which on the web is moot
// anyway — settings are shared live across frames there, so nothing in them can tell the clones
// apart. Per-slide identity on web comes from two places instead, both written below:
//   - the Rich API TAGS, which the edit-view load path reads (openSlideFromHostSlideTag), and
//   - the common-slide localStorage bridge (AHA-43111), which is the ONLY one of the two that
//     survives into the Slide Show session, where the Rich API throws and the tags are unreadable.
//
// Signature deliberately mirrors runDesktopInsert so the caller can dispatch between them
// without knowing anything about either.
const runWebInsert = ({
  presentationId,
  slideIds,
  snapshotPngBySlideId,
  hostSlideIndex,
  insertedSlideIds,
  keepHostSlide,
  onHostResolved,
}) => window.PowerPoint.run(async (context) => {
  const { presentation } = context
  const { slides } = presentation

  const hostSlide = slides.getItemAt(hostSlideIndex - 1)
  hostSlide.load('id')
  const donor = hostSlide.exportAsBase64()
  // Piggy-backed on the sync we already have to pay for: this is the "before" list the
  // first iteration diffs against, so the loop never needs a load of its own up front.
  slides.load('items/id')
  await context.sync()

  const hostSlideId = hostSlide.id
  onHostResolved(hostSlideId)
  const donorBase64 = donor.value
  // Running id list, carried across iterations: each iteration's "after" becomes the
  // next iteration's "before", so we load the collection once per insert instead of twice.
  let knownSlideIds = slides.items.map((slide) => slide.id)

  // One insert per extra selection, each targeting the previously inserted slide, so
  // the clones land in selection order directly after the add-in's own slide.
  let targetSlideId = hostSlideId
  // keep-host mode clones the first selected slide too — see insertSlidesForSelection.
  const firstCloneIndex = keepHostSlide ? 0 : 1
  for (let i = firstCloneIndex; i < slideIds.length; i += 1) {
    // Sequential by design: each insert targets the slide the PREVIOUS iteration created,
    // so its id has to be resolved before the next insert can be issued.
    // eslint-disable-next-line no-await-in-loop
    const cloneInstanceId = await insertDonorWithSnapshot(
      context,
      donorBase64,
      snapshotPngBySlideId[slideIds[i]],
      targetSlideId,
      generateAddinInstanceId(),
    )
    // eslint-disable-next-line no-await-in-loop
    const after = await loadSlideIds(context, slides)

    // Snapshot into a per-iteration const: the diff closure must not capture the mutable
    // running list (no-loop-func).
    const before = knownSlideIds
    const insertedId = resolveInsertedId(after, before, targetSlideId)
    if (!insertedId) {
      throw new Error(UNRESOLVED_INSERT_ERROR)
    }
    insertedSlideIds.push(insertedId)
    knownSlideIds = after
    targetSlideId = insertedId

    // Tag the clone HERE, not in a batch after the loop. On Office web,
    // insertSlidesFromBase64 materialises the slide as soon as its sync lands and
    // PowerPoint activates the content add-in on it immediately — that frame then
    // resolves its binding from its host slide's tags exactly once, at load. Tagging
    // every clone only at the end of the batch left clone #1 untagged for N-1 further
    // sync round-trips, so it could load, find no tag, fall through to the shared
    // document.settings (still holding the pre-Add binding), and render the WRONG
    // AhaSlides slide — permanently, since the binding is never re-read. Queuing the
    // tag now means it rides the very next context.sync() instead.
    tagSlide(slides.getItem(insertedId), presentationId, slideIds[i])

    // AHA-43111 read-view bridge, keyed by the Common-API id derived from the Rich id we just
    // resolved for this clone. Written per iteration for the same reason the tag is: the clone's
    // add-in frame boots as soon as its insert lands, and the Slide Show it may enter next can
    // read nothing else. No Office call — see toCommonSlideId.
    savePptCommonSlideBinding(toCommonSlideId(insertedId), { presentationId, slideId: slideIds[i] })
    // AHA-48826: the binding the clone's Slide Show frame will actually find, under the instance id
    // it was just given — no document.url needed. When the rewrite did not take, the clone shares the
    // donor's instance, so it is recorded under that one instead: the read side then sees one instance
    // with two differently bound slides and reports it ambiguous rather than picking one.
    savePptInstanceBinding(
      cloneInstanceId || readOwnAddinInstanceId(),
      toSlideToken(insertedId),
      { presentationId, slideId: slideIds[i] },
      { commonSlideId: toCommonSlideId(insertedId) },
    )
  }

  // keep-host mode: the host keeps the binding it came in with, so it gets no tag here — but the
  // LAST clone's tag is still only queued (the loop tags after its final sync), so the batch has
  // to be flushed before this returns or that clone materialises unbound.
  if (keepHostSlide) {
    await context.sync()
    return
  }

  // The host slide is the frame doing the inserting: it is already bound (its own
  // document.settings binding is written by the caller) and cannot race a clone, so its
  // tag can ride the final sync. Until this lands the clones are unbound copies of the
  // donor, so a failure anywhere in this batch must still roll back.
  tagSlide(slides.getItem(hostSlideId), presentationId, slideIds[0])
  await context.sync()

  savePptCommonSlideBinding(toCommonSlideId(hostSlideId), { presentationId, slideId: slideIds[0] })
  // The host keeps its own instance id, which is this frame's.
  savePptInstanceBinding(
    readOwnAddinInstanceId(),
    toSlideToken(hostSlideId),
    { presentationId, slideId: slideIds[0] },
    { commonSlideId: toCommonSlideId(hostSlideId) },
  )
})

// Clones the add-in's own slide once per extra selection.
// slideIds are AhaSlides slide ids in presentation order: slideIds[0] binds to the slide
// the add-in is already on; each of the rest gets a new PowerPoint slide, inserted in
// order immediately after it.
//
// AHA-47221 keepHostSlide: the add-in's own slide is left alone and EVERY selected slide gets a
// new PowerPoint slide, slideIds[0] included. That is what the presenting canvas's "Add slides"
// needs. The frame asking for them there is mid-session on a slide of its own, and the default
// behaviour would hand that slide to slideIds[0] — the slide the presenter is standing on would
// silently become a different one, and the audience would follow it there. Adding slides is not
// a request to replace the slide you are adding them from.
//
// The host's tags are still stripped before the donor export either way, because a clone that
// inherits them boots holding the DONOR's binding; keep-host mode snapshots them first and puts
// them back — with desktop's document.settings — once the insert has settled, on the failure
// path as well as the success one.
//
// snapshotPngBySlideId (optional, AhaSlides slide id -> PNG bytes) becomes each NEW slide's add-in
// snapshot and fallback picture. The host slide is never re-inserted, so outside keep-host mode
// slideIds[0]'s entry has nowhere to go.
export const insertSlidesForSelection = async ({
  presentationId,
  slideIds,
  keepHostSlide = false,
  snapshotPngBySlideId = {},
}) => {
  if (!isBulkSlideAddSupported()) {
    throw new Error('Bulk slide add is not supported on this version of PowerPoint')
  }
  if (!slideIds || !slideIds.length) {
    throw new Error('No slides selected')
  }

  const hostSlideIndex = await readHostSlideIndex('insert')
  const insertedSlideIds = []
  let hostSlideId = null

  Sentry.addBreadcrumb({
    category: OFFICE_BREADCRUMB_CATEGORY,
    message: `insertSlidesForSelection: ${slideIds.length} slide(s)`,
    level: 'info',
  })

  // Taken BEFORE the strip below, while the host slide still holds its own binding. keep-host
  // mode is the only thing that ever restores it, so it is the only thing that pays for reading it.
  const hostBinding = keepHostSlide ? await readHostBindingSnapshot(hostSlideIndex) : null

  // Clean the donor before exporting it, so clones can't inherit a previous Add's binding.
  await stripHostSlideTags(hostSlideIndex)

  // Desktop bakes a per-clone settings snapshot; the web reuses one shared export and carries
  // identity in the tags plus the common-slide bridge. The two share a signature, so dispatching
  // is a lookup rather than a branch through this function.
  const desktop = isDesktopOfficeHost()
  log('insert path |', desktop ? 'desktop (baked per-clone settings)' : 'web (tags + common-slide bridge)')

  const run = (desktop ? runDesktopInsert : runWebInsert)({
    presentationId,
    slideIds,
    snapshotPngBySlideId,
    hostSlideIndex,
    insertedSlideIds,
    keepHostSlide,
    onHostResolved: (id) => { hostSlideId = id },
  })

  try {
    await withTimeout(run, insertTimeoutMs(slideIds.length), 'Timed out while inserting PowerPoint slides')
  } catch (error) {
    log('insert failed', error)
    // withTimeout can only stop WAITING for the batch — Office.js has no cancellation, so
    // on a timeout the run may still be inserting slides. Let it settle before rolling
    // back, otherwise this snapshot of insertedSlideIds is stale and we orphan whatever
    // lands after it. That settle-wait is itself bounded by SETTLE_GRACE_MS: a frozen or
    // crashed host may never settle `run` at all, and this function now backs a UI spinner,
    // so an unbounded wait here would hang the caller forever with no way to show an error.
    // Roll back with whatever insertedSlideIds holds once the grace period elapses either
    // way — trading a small residual orphan-slide risk in the frozen-host case (acceptable:
    // if the host is truly frozen, nothing further is landing anyway) for a caller that
    // always gets a bounded, actionable failure. Either way the ORIGINAL error is what we
    // throw below, never a rollback error or a grace-timeout error.
    await waitForSettleWithGrace(run, SETTLE_GRACE_MS)
    await rollbackInsertedSlides(insertedSlideIds, hostSlideId)
    // keep-host mode restores the host from its own snapshot instead of everything below: the
    // binding to put back is the one it came in with, never slideIds[0]. rollbackInsertedSlides
    // has just stripped its tags (correctly — it cannot know which mode it is undoing), and on
    // desktop the loop left settings holding whichever clone it was baking, so both legs matter.
    if (keepHostSlide) {
      await restorePreservedHostBinding(hostSlideIndex, hostBinding, desktop)
      throw error
    }
    // The desktop loop leaves document.settings holding whichever clone it was baking when it
    // failed. Those settings belong to the HOST frame: left pointing at a clone's slide, the host
    // would come back bound to a slide the user was told had failed to add. Only the desktop loop
    // moves them, so only desktop has anything to put back. Best-effort — a restore failure must
    // not mask the original error.
    if (desktop) {
      try {
        await restoreHostBinding(presentationId, slideIds[0])
      } catch (restoreError) {
        log('restore host binding after failed insert failed', restoreError)
        Sentry.captureException(restoreError)
      }
    }
    throw error
  }

  if (keepHostSlide) {
    await restorePreservedHostBinding(hostSlideIndex, hostBinding, desktop)
  }

  log('inserted', insertedSlideIds, 'host =', hostSlideId)
  return { hostSlideId, insertedSlideIds }
}

// AHA-47221: ONE new PowerPoint slide, bound to a WHOLE presentation.
//
// The other half of the presenting canvas's "⋮" menu, and the same promise keepHostSlide makes
// for slides: the add-in's own slide is left exactly as it was. The frame asking for this is
// mid-session on a slide of its own, so rebinding it — which is what the ordinary "Add
// presentation" flow does — would replace the slide the presenter is standing on with the whole
// of another deck, and take the audience with it.
//
// A whole-presentation binding is NOT a tag. Its only representation is document.settings
// ({activePresentationId}, no slide id — see handleDataAfterLoadOffice), and tags outrank
// settings on load, so the new slide must carry none: a clone that inherited the donor's tags
// would come up as a single-slide frame instead. It is tagless twice over — the donor is
// stripped before the export, and the clone is cleared again inside the batch, because the strip
// is best-effort and this is the one binding a leftover tag would silently win.
//
// Where the binding lives, and why the two platforms end differently:
//   - DESKTOP: settings are per-frame and a clone inherits a SNAPSHOT of them taken at export, so
//     writing them before the export bakes the whole-presentation binding into the new slide.
//     The host frame's own live settings are then put back, exactly as the desktop insert loop
//     does after baking its clones.
//   - WEB: settings are shared LIVE across every add-in frame (office-js #2765), so there is
//     nothing to bake and nothing per-frame to restore — the bag itself is what the new frame
//     reads at boot, and it has to still hold the whole-presentation binding when it gets there.
//     The host frame is unaffected because its identity on the web comes from its host-slide TAG,
//     which outranks settings. Any OTHER untagged frame in the deck will resolve to this
//     presentation on its next load, which is what a whole-presentation binding has always meant
//     on the web — this does not make it more true, it just makes it true from a new slide.
//
// Nothing is published and nothing is navigated: the presentation is not being presented yet, and
// this frame is still presenting what it was. The new slide's own frame does all of that when the
// show reaches it.
export const insertPresentationSlide = async ({ presentationId }) => {
  if (!isBulkSlideAddSupported()) {
    throw new Error('Bulk slide add is not supported on this version of PowerPoint')
  }
  if (!presentationId) {
    throw new Error('No presentation selected')
  }

  await persistInviteCodeBeforeInsert(presentationId)

  const hostSlideIndex = await readHostSlideIndex('insert-presentation')
  const insertedSlideIds = []
  let hostSlideId = null

  Sentry.addBreadcrumb({
    category: OFFICE_BREADCRUMB_CATEGORY,
    message: `insertPresentationSlide: presentation ${presentationId}`,
    level: 'info',
  })

  // Taken while the host still holds its own binding, and before anything below moves either
  // half of it: the tags are stripped for the export, and the settings are overwritten with the
  // binding the new slide needs.
  const hostBinding = await readHostBindingSnapshot(hostSlideIndex)
  await stripHostSlideTags(hostSlideIndex)

  const desktop = isDesktopOfficeHost()
  const binding = { activePresentationId: toNumericId(presentationId) }
  log('insert presentation slide |', desktop ? 'desktop (baked settings)' : 'web (shared settings)', binding)

  // BEFORE the export on both platforms, for two different reasons: desktop needs it inside the
  // snapshot the clone inherits, and on the web the clone's add-in frame boots the moment the
  // insert syncs, so a write that came afterwards would race it.
  await saveDocumentSetting(DATA_SLIDE_SETTING_KEY, binding)

  // AHA-48826 (web): the new slide gets its own add-in instance id, like an "Add N slides" clone, so
  // its Slide Show frame finds this whole-presentation binding rather than the host's slide.
  let cloneInstanceId = null
  const run = window.PowerPoint.run(async (context) => {
    const { presentation } = context
    const { slides } = presentation

    const hostSlide = slides.getItemAt(hostSlideIndex - 1)
    hostSlide.load('id')
    const donor = hostSlide.exportAsBase64()
    slides.load('items/id')
    await context.sync()

    hostSlideId = hostSlide.id
    const before = slides.items.map((slide) => slide.id)

    const freshInstanceId = desktop ? null : generateAddinInstanceId()
    const donorBase64 = await withAddinInstanceId(donor.value, freshInstanceId)
    if (donorBase64 !== donor.value) {
      cloneInstanceId = freshInstanceId
    }
    presentation.insertSlidesFromBase64(donorBase64, { targetSlideId: hostSlideId })
    await context.sync()
    const after = await loadSlideIds(context, slides)

    const insertedId = resolveInsertedId(after, before, hostSlideId)
    if (!insertedId) {
      throw new Error(UNRESOLVED_INSERT_ERROR)
    }
    insertedSlideIds.push(insertedId)

    // Belt and braces (see above): the donor strip is best-effort, and a surviving tag would
    // outrank the binding this slide exists to carry.
    clearTagsOnSlide(slides.getItem(insertedId))
    await context.sync()
  })

  try {
    await withTimeout(run, insertTimeoutMs(1), 'Timed out while inserting a PowerPoint slide')
  } catch (error) {
    log('insert presentation slide failed', error)
    Sentry.captureException(error)
    // Same reasoning as insertSlidesForSelection: Office.js cannot be cancelled, so let a
    // timed-out batch settle — bounded — before rolling back what it left behind.
    await waitForSettleWithGrace(run, SETTLE_GRACE_MS)
    await rollbackInsertedSlides(insertedSlideIds, hostSlideId)
    // Settings go back on BOTH platforms here, unlike the success path: with no new slide left
    // to read them, a settings bag still holding the whole-presentation binding would just be
    // this frame's own binding overwritten — on the web that is the only copy there is, and the
    // host frame would come back as the presentation it failed to add.
    await restorePreservedHostBinding(hostSlideIndex, hostBinding, true)
    throw error
  }

  // AHA-43111 read-view bridge, document-wide because a whole-presentation binding has no slide
  // to key a per-slide entry by. The host slide is unaffected: its own per-slide entry is checked
  // first in the Slide Show, and this is only reached by a slide that has none — which is exactly
  // the new one. Web-only; a no-op everywhere else.
  savePptOnlineBinding(binding)
  // AHA-48826: what the new slide's Slide Show frame can read when document.url is empty. Under the
  // donor's instance when the rewrite did not take, where it makes that instance ambiguous.
  const [newSlideId] = insertedSlideIds
  savePptInstanceBinding(cloneInstanceId || readOwnAddinInstanceId(), toSlideToken(newSlideId), binding, {
    commonSlideId: toCommonSlideId(newSlideId),
  })

  // Settings are written directly here rather than through saveDataToSlidePowerPoint, so the
  // invite code for the added presentation is stored explicitly.
  persistInviteCodeAfterInsert(presentationId)

  // Tags always, settings only on desktop — on the web they ARE the new slide's binding.
  await restorePreservedHostBinding(hostSlideIndex, hostBinding, desktop)

  log('inserted presentation slide', insertedSlideIds, 'host =', hostSlideId)
  return { hostSlideId, insertedSlideIds }
}

// Removes this frame's binding from its own host slide's tags.
//
// The slide tag is the HIGHEST-precedence binding source on load (it has to be — it is the
// only thing that can tell cloned frames apart). That cuts both ways: when the user
// deliberately LEAVES a presentation, clearing document.settings and the last-selected
// presentation tag is not enough, because the slide tag outranks both. The editor's back
// path does a full page reload, so the next load would read the leftover tag and route
// straight back into the presentation the user just left — trapping them in it with no way
// out. Anything that unbinds a frame must therefore clear this tag too.
//
// Best-effort and never throws: failing to clear a tag must not block navigation.
export const clearSlideBindingFromHostSlide = async () => {
  if (!canReadSlideTags()) {
    return
  }
  try {
    const hostSlideIndex = await readHostSlideIndex('clear')
    await window.PowerPoint.run(async (context) => {
      clearTagsOnSlide(context.presentation.slides.getItemAt(hostSlideIndex - 1))
      await context.sync()
    })
    log('cleared host slide binding')
  } catch (error) {
    log('clear host slide binding failed', error)
    Sentry.captureException(error)
  }
}

// Mirrors a frame's document.settings binding onto its own host PowerPoint slide's tags, so
// the two never diverge. Called centrally from saveDataToSlidePowerPoint (office/index.js)
// on EVERY dataSlidePPT write.
//
// Why this has to exist: the slide tag is the highest-precedence binding source on load and
// the ONLY one a cloned frame can read. Before this, only the selector's insert path wrote
// it, so a later rebind through the ordinary editor buttons (which write settings only) was
// silently reverted by the stale tag on the next load, and clearing settings on "leave the
// editor" left the tag behind to trap the user in the presentation they'd left. Keeping the
// tag in step with every settings write closes both.
//
// A single-slide binding ({presentationId, slideId}) writes both tags; a whole-presentation
// binding ({activePresentationId}, no single slide) or a clear (null) removes them, so the
// tag never outranks settings with a slide the binding no longer points at. Resolves the
// target via getSelectedDataAsync — reliable for the editor writes that call this, where the
// frame's own slide is the selected one. Best-effort, never throws.
//
// AHA-43111: returns the Common-API id of the slide it tagged (null when it could not tag one),
// so the caller can key the read-view bridge off the SAME slide instead of taking a second,
// independently-racing selection reading. The id comes free — one extra property on a sync the
// tag write already had to make.
export const writeSlideBindingToHostSlide = async (value) => {
  if (!canReadSlideTags()) {
    return null
  }
  const presentationId = value && value.presentationId
  const slideId = value && value.slideId
  let commonSlideId = null
  try {
    const hostSlideIndex = await readHostSlideIndex('mirror')
    await window.PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItemAt(hostSlideIndex - 1)
      slide.load('id')
      if (presentationId && slideId) {
        tagSlide(slide, presentationId, slideId)
      } else {
        clearTagsOnSlide(slide)
      }
      await context.sync()
      commonSlideId = toCommonSlideId(slide.id)
    })
    log('mirrored binding to host slide', presentationId, slideId, '| common id', commonSlideId)
  } catch (error) {
    log('mirror binding to host slide failed', error)
    Sentry.captureException(error)
  }
  return commonSlideId
}

// Reads this frame's binding off its own host slide's tags. Returns null — never throws —
// so the load path can fall through to the legacy document.settings binding.

// One tag-read batch for the slide THIS frame lives on, identified by the persistent id `slideToken`
// pulled from window.name (web path). Loads every slide's id, matches the token against the part of
// the Rich API id after '#' ("257#<token>"), then reads that slide's tags. Resolves the binding, or
// null when no slide matches / the matched slide carries no tags; rejects on a batch error.
//
// AHA-43111: the resolved binding also carries `commonSlideId`, the OTHER half of the same id we
// just matched on. Having found this frame's own slide by a per-frame token, we hold its Rich id —
// so its Common-API id is a free string split (toCommonSlideId), with none of the document-global
// ambiguity of asking getSelectedDataAsync. That makes every edit-view cold load able to (re)stamp
// the read-view bridge for its own slide, which is what keeps the bridge correct after a rebind
// that never went through the insert path. Web path only; the desktop read returns the bare binding.
const readTagsBySlideToken = (slideToken) => window.PowerPoint.run(async (context) => {
  const { slides } = context.presentation
  slides.load('items/id')
  await context.sync()
  const slide = slides.items.find((item) => String(item.id).split('#')[1] === slideToken)
  if (!slide) {
    return null
  }
  const resolveBinding = queueBindingTagRead(slide)
  await context.sync()
  const binding = resolveBinding()
  return binding && { ...binding, commonSlideId: toCommonSlideId(slide.id) }
})

// Bounded retry around a single-batch tag read. A hung context.sync() (concurrent web frames) must
// not block cold-load, so each attempt is bounded by BINDING_READ_TIMEOUT_MS and a timed-out batch
// is abandoned — its late rejection swallowed so it can't surface as an unhandled rejection.
// `readOnce` returns a fresh PowerPoint.run promise per attempt; `label` names the target for the trace.
const readBindingWithRetry = async (readOnce, label) => {
  for (let attempt = 1; attempt <= BINDING_READ_MAX_ATTEMPTS; attempt += 1) {
    const startedAt = performance.now()
    log('binding-read start |', label, '| attempt', attempt)
    const run = readOnce()
    run.catch(() => {})
    try {
      // eslint-disable-next-line no-await-in-loop -- attempts are inherently sequential (retry)
      const binding = await withTimeout(run, BINDING_READ_TIMEOUT_MS, 'host-slide tag read timed out')
      log('host slide binding =', binding, '| synced in', Math.round(performance.now() - startedAt), 'ms')
      return binding
    } catch (error) {
      log(`binding-read attempt ${attempt} failed after ${Math.round(performance.now() - startedAt)}ms`, error)
      if (attempt < BINDING_READ_MAX_ATTEMPTS) {
        // eslint-disable-next-line no-await-in-loop -- deliberate back-off between attempts
        await delay(BINDING_READ_RETRY_DELAY_MS)
      }
    }
  }
  return null
}

export const readSlideBindingFromHostSlide = async () => {
  if (!canReadSlideTags()) {
    return null
  }

  // PowerPoint web: resolve THIS frame's own slide from window.name (per-frame, race-free) and read
  // that slide's tags. getSelectedDataAsync is deliberately NOT a fallback here — it is
  // document-global, so on a multi-frame insert it mis-resolves simultaneously-booting clones to
  // each other's (or the host's) slide, which is the exact AHA-43111 defect this replaces. No token
  // means we cannot safely identify this frame's slide, so return null and let cold-load try its
  // other binding sources rather than guess a wrong slide.
  if (isPptOnlineAddin()) {
    const slideToken = readOwnSlideTokenFromFrameName()
    if (!slideToken) {
      log('no slide token in window.name -> cannot resolve own slide (web)')
      // The token's shape is an undocumented Office implementation detail, so this can start
      // failing for every web frame the day PowerPoint renames its add-in iframes — and the
      // failure is silent: the frame degrades to the document-global sources this replaced and
      // resolves the WRONG slide, which looks like a product bug, not a platform change. A
      // breadcrumb is the only way that shows up in the field; a debug log nobody has enabled
      // is not. Breadcrumb rather than captureException: on its own this is not an error, it
      // just makes the next reported one legible.
      Sentry.addBreadcrumb({
        category: OFFICE_BREADCRUMB_CATEGORY,
        message: 'readSlideBindingFromHostSlide: no slide token in window.name (web)',
        level: 'warning',
      })
      return null
    }
    return readBindingWithRetry(() => readTagsBySlideToken(slideToken), `slideToken ${slideToken}`)
  }

  // Desktop and any non-web host: a single frame with no selection race, so the selected slide IS
  // this frame's slide — resolve it by index exactly as before.
  let hostSlideIndex
  try {
    hostSlideIndex = await readHostSlideIndex('read')
  } catch (error) {
    log('read host slide binding failed (index)', error)
    return null
  }
  return readBindingWithRetry(() => readTagsAtSlideOnce(hostSlideIndex), `hostSlideIndex ${hostSlideIndex}`)
}
